import { CARE_SECTION_KEYS } from "./careTypes";
import type { CareLang, CareSectionKey, CareSections, CareText, ResolvedCare, ResolvedSection } from "./careTypes";

export const isBlankText = (text?: CareText | null): boolean =>
  !text || (!String(text.en || "").trim() && !String(text.hi || "").trim());

/** The certificate language switcher may be en, hi or te; care has en and hi, so anything else reads English. */
export const careLang = (language?: string | null): CareLang =>
  String(language || "").toLowerCase().startsWith("hi") ? "hi" : "en";

/** Text in the wanted language, falling back to the other one so a section is never blank. */
export const pickText = (text: CareText | null | undefined, lang: CareLang): string => {
  if (!text) return "";
  const wanted = String((lang === "hi" ? text.hi : text.en) || "").trim();
  if (wanted) return wanted;
  return String((lang === "hi" ? text.en : text.hi) || "").trim();
};

/** One instruction per line; leading bullets typed by hand are dropped. */
export const careLines = (raw: string): string[] =>
  String(raw || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:[-*•·]|\d+[.)])\s+/, "").trim())
    .filter(Boolean);

export const filledSectionCount = (sections?: CareSections | null): number =>
  CARE_SECTION_KEYS.filter((key) => !isBlankText(sections?.[key])).length;

/** Same rule as the server: an override key wins (blank hides the section), else the template snapshot. */
export const resolveCare = (
  snapshot: CareSections | null | undefined,
  overrides: CareSections | null | undefined,
  additionalNotes?: CareText | null
): ResolvedCare => {
  const sections: ResolvedSection[] = [];
  CARE_SECTION_KEYS.forEach((key: CareSectionKey) => {
    const hasOverride = Boolean(overrides && Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key]);
    const text = hasOverride ? overrides![key] : snapshot?.[key];
    if (isBlankText(text)) return;
    sections.push({ key, en: text?.en ?? null, hi: text?.hi ?? null });
  });
  return { sections, additionalNotes: isBlankText(additionalNotes) ? null : additionalNotes };
};

export const emptySections = (): CareSections =>
  CARE_SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: { en: "", hi: "" } }), {} as CareSections);

export const sameSections = (a?: CareSections | null, b?: CareSections | null): boolean =>
  CARE_SECTION_KEYS.every((key) => {
    const x = a?.[key];
    const y = b?.[key];
    return String(x?.en || "").trim() === String(y?.en || "").trim() && String(x?.hi || "").trim() === String(y?.hi || "").trim();
  });

/** Public link printed on the care card: the certificate verification entry. */
export const certificateUrl = (productUniqueId: string): string => {
  const origin = String(
    process.env.REACT_APP_LOC_URL || process.env.REACT_APP_DEV_URL || (typeof window !== "undefined" ? window.location.origin : "")
  ).replace(/\/$/, "");
  const id = String(productUniqueId || "").trim();
  return id ? `${origin}/product-verifing/${encodeURIComponent(id)}` : "";
};
